/**
 * Dex sync health — the signal that was missing.
 *
 * WHY THIS EXISTS
 *   Three separate bugs shipped in which a dex write succeeded on device and
 *   never reached the server, and all three looked identical from the app: the
 *   grid updated, nothing complained, and the backup silently stopped taking
 *   writes.
 *
 *     - the endpoint rejected four variants the client can store, so ONE such
 *       row failed the whole batch with 400 and wedged the queue permanently
 *     - the flush sent every pending row in one body, which the server rejects
 *       past 500 entries, so a Quick-update over a region never synced at all
 *     - the pending set is in-memory, so any failure plus an app restart threw
 *       the queue away and the writes were never retried
 *
 *   Each was found only because a number looked wrong on a screenshot. The
 *   common factor is not the individual bug — it is that a failing backup
 *   produced no observable difference from a working one.
 *
 * WHAT IT DOES
 *   Records the outcome of every flush attempt, DURABLY (localStorage), so the
 *   signal survives the restart that discards the in-memory queue. That
 *   persistence is the point: an in-memory-only health record would vanish in
 *   exactly the situation it exists to report.
 *
 *   `unsentSince` is the load-bearing field. It is set when a write first
 *   fails to land and cleared by a flush whose every batch lands, so "my dex
 *   has been out of sync since 09:14" survives restarts and answers the
 *   question the three bugs above all raised.
 *
 *   A landed flush that still leaves rows queued is NOT a failure. The flush
 *   snapshots the queue before it starts, so anything left afterwards arrived
 *   mid-flight: that is the normal shape of a long scan, where the queue is
 *   never empty at the end of a flush. The clock restarts at that flush
 *   rather than running from the first one (2026-09-09: seven devices on the
 *   admin dashboard read "failing" for weeks while every batch had landed).
 *
 * WHAT IT DELIBERATELY DOES NOT DO
 *   It does not retry, reorder or repair anything. It only reports. Recovery
 *   belongs to the flush paths; conflating the two is how a monitor ends up
 *   causing the outage it was meant to observe.
 */

export const SYNC_HEALTH_KEY = "nexus.dexSyncHealth.v1";
export const SYNC_HEALTH_EVENT = "nexus:dex-sync-health";

/** Why a flush attempt did not land. Kept coarse — enough to act on, no PII. */
export type SyncFailureReason =
  /** Non-2xx that is not the "endpoint absent" case: a real rejection. */
  | "rejected"
  /** 404/501 — the endpoint or migration is not there yet. */
  | "endpoint_missing"
  /** fetch threw: offline, DNS, TLS, timeout. */
  | "network";

export interface SyncHealth {
  /** Last time a flush landed every batch it sent. */
  lastSuccessAt: number | null;
  lastFailureAt: number | null;
  lastFailureReason: SyncFailureReason | null;
  /** HTTP status when the failure had one. */
  lastFailureStatus: number | null;
  /** Consecutive failed attempts; reset by a flush that lands every batch. */
  consecutiveFailures: number;
  /**
   * When writes first stopped landing, cleared only when the queue drains.
   * Survives restarts, which the in-memory pending queue does not.
   */
  unsentSince: number | null;
  /** Rows still queued at the last attempt (0 once drained). */
  pendingAtLastAttempt: number;
}

function empty(): SyncHealth {
  return {
    lastSuccessAt: null,
    lastFailureAt: null,
    lastFailureReason: null,
    lastFailureStatus: null,
    consecutiveFailures: 0,
    unsentSince: null,
    pendingAtLastAttempt: 0,
  };
}

/** Total — never throws; a broken blob reads as empty rather than crashing sync. */
export function readSyncHealth(): SyncHealth {
  if (typeof localStorage === "undefined") return empty();
  try {
    const raw = localStorage.getItem(SYNC_HEALTH_KEY);
    if (!raw) return empty();
    const p = JSON.parse(raw) as Partial<SyncHealth> | null;
    if (!p || typeof p !== "object") return empty();
    const base = empty();
    return {
      lastSuccessAt: typeof p.lastSuccessAt === "number" ? p.lastSuccessAt : base.lastSuccessAt,
      lastFailureAt: typeof p.lastFailureAt === "number" ? p.lastFailureAt : base.lastFailureAt,
      lastFailureReason: (p.lastFailureReason as SyncFailureReason) ?? base.lastFailureReason,
      lastFailureStatus: typeof p.lastFailureStatus === "number" ? p.lastFailureStatus : base.lastFailureStatus,
      consecutiveFailures: typeof p.consecutiveFailures === "number" ? p.consecutiveFailures : base.consecutiveFailures,
      unsentSince: typeof p.unsentSince === "number" ? p.unsentSince : base.unsentSince,
      pendingAtLastAttempt: typeof p.pendingAtLastAttempt === "number" ? p.pendingAtLastAttempt : base.pendingAtLastAttempt,
    };
  } catch {
    return empty();
  }
}

function write(next: SyncHealth): void {
  if (typeof localStorage !== "undefined") {
    try { localStorage.setItem(SYNC_HEALTH_KEY, JSON.stringify(next)); } catch { /* quota */ }
  }
  if (typeof window !== "undefined") {
    try { window.dispatchEvent(new CustomEvent(SYNC_HEALTH_EVENT)); } catch { /* jsdom */ }
  }
}

/**
 * A flush attempt finished. `pendingAfter` is what remains queued. With no
 * failure it is only ever edits that arrived mid-flight (the flush snapshots
 * the queue first), so it sets how old the backlog is, not whether there is a
 * fault.
 */
export function recordSyncAttempt(input: {
  posted: number;
  pendingAfter: number;
  failure?: { reason: SyncFailureReason; status?: number } | null;
  now?: number;
}): SyncHealth {
  const now = input.now ?? Date.now();
  const prev = readSyncHealth();
  const next: SyncHealth = { ...prev, pendingAtLastAttempt: input.pendingAfter };

  if (input.failure) {
    next.lastFailureAt = now;
    next.lastFailureReason = input.failure.reason;
    next.lastFailureStatus = input.failure.status ?? null;
    next.consecutiveFailures = prev.consecutiveFailures + 1;
    next.unsentSince = prev.unsentSince ?? now;
    // Loud on purpose. A silent backup failure is the whole problem; this line
    // is what makes it greppable in a device log.
    console.warn("[dex-sync] flush did not fully land", {
      reason: input.failure.reason,
      status: input.failure.status ?? null,
      posted: input.posted,
      stillQueued: input.pendingAfter,
      unsentSince: next.unsentSince,
      consecutiveFailures: next.consecutiveFailures,
    });
  } else {
    // Every batch landed, so any earlier failure is over. What is still
    // queued arrived while the request was in the air (the flush snapshots
    // the whole queue before it starts), which is what a long scan looks
    // like: rows keep coming, so the queue is never empty at the end of a
    // flush. The old rule cleared the clock ONLY on an empty queue, so it ran
    // from the first flush of a scan until the app closed and, five minutes
    // in, a device whose every batch was landing reported itself broken.
    //
    // The remainder is real but only as old as this flush, and the next
    // debounce drains it. If the app is killed before that debounce, the
    // in-memory queue is gone and "unsent since a moment ago, N rows" is
    // exactly the truth.
    next.lastSuccessAt = now;
    next.consecutiveFailures = 0;
    next.lastFailureReason = null;
    next.lastFailureStatus = null;
    next.unsentSince = input.pendingAfter === 0 ? null : now;
  }

  write(next);
  return next;
}

/**
 * Is the backup behind in a way worth telling the trainer about?
 *
 * Deliberately NOT "anything is queued": a flush is debounced by design and a
 * queue of a few rows for a few seconds is normal operation. A warning that
 * fires during healthy use is one users learn to ignore, which would leave the
 * real failure just as invisible as before.
 */
export const SYNC_STALE_MS = 5 * 60 * 1000;

export function isSyncUnhealthy(h: SyncHealth = readSyncHealth(), now: number = Date.now()): boolean {
  // A hard rejection is worth surfacing immediately: it will not fix itself.
  if (h.lastFailureReason === "rejected") return true;
  // Offline or a missing endpoint may resolve on its own, so give it a window
  // before nagging — but the backlog clock decides, not the retry count.
  if (h.unsentSince != null && now - h.unsentSince >= SYNC_STALE_MS) return true;
  return false;
}

/**
 * Push the local record to the server so the admin dashboard can see the fleet
 * rather than one phone at a time.
 *
 * REPORTS ON TRANSITIONS, NOT ON EVERY ATTEMPT. A flush fires on a debounce,
 * so reporting each one would put a request behind every dex edit — turning a
 * diagnostic into a load problem. What an operator needs is the edges: this
 * device just broke, or just recovered. A periodic re-report while broken
 * keeps `reported_at` fresh so a silent device is distinguishable from a
 * healthy one.
 *
 * Fire-and-forget and failure-tolerant: a health report must never surface an
 * error, block a flush, or recurse into recording its own outcome.
 */
const HEALTH_REPORT_KEY = "nexus.dexSyncHealth.reported.v1";
const HEALTH_REPORT_MIN_GAP_MS = 30 * 60 * 1000;

function lastReport(): { at: number; unhealthy: boolean } | null {
  if (typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(HEALTH_REPORT_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw) as { at?: number; unhealthy?: boolean } | null;
    if (!p || typeof p.at !== "number") return null;
    return { at: p.at, unhealthy: !!p.unhealthy };
  } catch {
    return null;
  }
}

export function shouldReportSyncHealth(
  health: SyncHealth = readSyncHealth(),
  now: number = Date.now(),
): boolean {
  const unhealthy = isSyncUnhealthy(health, now);
  const prev = lastReport();
  // Never reported: only bother the server if there is something to say.
  if (!prev) return unhealthy;
  // The edge in either direction is always worth sending.
  if (prev.unhealthy !== unhealthy) return true;
  // Still broken: re-report occasionally so reported_at does not go stale and
  // make a broken device look merely quiet.
  if (unhealthy && now - prev.at >= HEALTH_REPORT_MIN_GAP_MS) return true;
  return false;
}

export async function reportSyncHealth(
  apiFetch: (method: string, url: string, data?: unknown) => Promise<Response>,
  now: number = Date.now(),
): Promise<{ reported: boolean }> {
  const health = readSyncHealth();
  if (!shouldReportSyncHealth(health, now)) return { reported: false };
  try {
    const res = await apiFetch("POST", "/api/dex/sync-health", {
      lastSuccessAt: health.lastSuccessAt,
      lastFailureAt: health.lastFailureAt,
      lastFailureReason: health.lastFailureReason,
      lastFailureStatus: health.lastFailureStatus,
      consecutiveFailures: health.consecutiveFailures,
      unsentSince: health.unsentSince,
      pendingAtLastAttempt: health.pendingAtLastAttempt,
    });
    if (!res.ok) return { reported: false };
    if (typeof localStorage !== "undefined") {
      try {
        localStorage.setItem(
          HEALTH_REPORT_KEY,
          JSON.stringify({ at: now, unhealthy: isSyncUnhealthy(health, now) }),
        );
      } catch { /* quota */ }
    }
    return { reported: true };
  } catch {
    // Offline, or the very outage being reported. Retried on the next edge.
    return { reported: false };
  }
}

/** Test seam. */
export function _resetSyncHealthForTests(): void {
  if (typeof localStorage !== "undefined") {
    try {
      localStorage.removeItem(SYNC_HEALTH_KEY);
      localStorage.removeItem(HEALTH_REPORT_KEY);
    } catch { /* ignore */ }
  }
}
